import React, { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const Register: React.FC = () => {
  const { register, user } = useAuth();
  const [form, setForm] = useState({
    nom: '', prenom: '', email: '', motDePasse: '',
    companyRaisonSociale: '', companyMatriculeFiscal: '', companyAdresse: '',
  });
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const on = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm({ ...form, [k]: e.target.value });

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await register(form);
      toast.success('Compte créé');
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Échec de l'inscription");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-green-50 px-4 py-10">
      <div className="w-full max-w-xl">
        <div className="bg-white rounded-2xl shadow-lg border border-emerald-100 p-8">
          <div className="flex items-center gap-3 mb-8">
            <span className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow-sm">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6">
                <path d="M17 8C8 10 5.9 16.17 3.82 21.34L5.71 22l1-2.3c.5.12 1 .2 1.5.2C19 20 22 3 22 3c-1 2-8 2.25-13 3.25S2 11.5 2 13.5s1.75 3.75 1.75 3.75C7 8 17 8 17 8z" />
              </svg>
            </span>
            <div>
              <span className="text-2xl font-extrabold tracking-tight text-slate-900">GreenBill</span>
              <div className="text-xs text-emerald-600 font-medium">Facturation éco-responsable</div>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Créer un compte</h1>
          <p className="text-sm text-slate-500 mb-6">Créez votre société et votre compte administrateur.</p>

          <form onSubmit={submit} className="space-y-5">
            <fieldset className="space-y-4">
              <legend className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Vous</legend>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Nom" value={form.nom} onChange={on('nom')} autoComplete="family-name" />
                <Field label="Prénom" value={form.prenom} onChange={on('prenom')} autoComplete="given-name" />
                <Field label="Email" type="email" value={form.email} onChange={on('email')} autoComplete="email" placeholder="vous@entreprise.tn" />
                <Field label="Mot de passe" type="password" value={form.motDePasse} onChange={on('motDePasse')} autoComplete="new-password" placeholder="••••••••" />
              </div>
            </fieldset>

            <fieldset className="space-y-4">
              <legend className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Votre société</legend>
              <Field label="Raison sociale" value={form.companyRaisonSociale} onChange={on('companyRaisonSociale')} />
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Matricule fiscal" value={form.companyMatriculeFiscal} onChange={on('companyMatriculeFiscal')} placeholder="7/A/B/C/000" />
                <Field label="Adresse" value={form.companyAdresse} onChange={on('companyAdresse')} />
              </div>
            </fieldset>

            <button type="submit" disabled={busy}
                    className="w-full rounded-lg bg-gradient-to-r from-emerald-500 to-green-600 text-white font-semibold py-2.5 hover:from-emerald-600 hover:to-green-700 disabled:opacity-50 transition shadow-sm">
              {busy ? 'Création...' : 'Créer mon compte'}
            </button>
          </form>

          <p className="text-sm text-slate-500 mt-6 text-center">
            Déjà un compte ?{' '}
            <Link to="/login" className="text-emerald-600 font-semibold hover:text-emerald-700">Se connecter</Link>
          </p>
        </div>
        <p className="text-center text-xs text-slate-400 mt-6">
          © {new Date().getFullYear()} GreenBill — Tous droits réservés
        </p>
      </div>
    </div>
  );
};

interface FieldProps {
  label: string;
  type?: string;
  value: string;
  onChange: React.ChangeEventHandler<HTMLInputElement>;
  placeholder?: string;
  autoComplete?: string;
}
const Field: React.FC<FieldProps> = ({ label, type = 'text', value, onChange, placeholder, autoComplete }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1">{label}</label>
    <input type={type} value={value} onChange={onChange} required
           placeholder={placeholder}
           autoComplete={autoComplete}
           className="w-full rounded-lg border border-slate-200 px-3 py-2.5 focus:outline-none focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100 transition" />
  </div>
);

export default Register;
